import { FC, useEffect, useState } from 'react';

import { Fade, Flex, Text } from '@chakra-ui/react';

import { useLazyCheckSpinQuery } from 'api/contest';

import { Loader } from 'components/Loader';

import { useAppSelector } from 'hooks/app';
import useBuilder from 'hooks/useBuilder';
import useContest from 'hooks/useContest';

import Builder from './ui/Builder';
import Spinner from './ui/Spinner/Spinner';
import Tutorial from './ui/Tutorial';

const BuilderPage: FC = () => {
  const [isSpinOpen, setIsSpinOpen] = useState<boolean | null>(null);

  const [showTutorial, setShowTutorial] = useState(false);
  const contest = useAppSelector((state) => state.contest.contest);
  const participation = useAppSelector((state) => state.contest.participation);

  const { fetchParticipation, isFetching: isFetchingParticipation } = useContest();

  useEffect(() => {
    if (contest && !participation) {
      fetchParticipation(contest);
    } else if (contest && participation?.contest?.id !== contest.id) {
      fetchParticipation(contest);
    }
  }, [contest, participation]);

  const { cardsWithStatus, selectedCards, card, benchCard, chooseCard, animation, isAnimating } = useBuilder({
    contest,
    participation,
  });

  const [checkSpin, { data: dailySpinStatus, isLoading: dailySpinStatusLoading }] = useLazyCheckSpinQuery();

  useEffect(() => {
    const lastShown = localStorage.getItem(`sbc_tutorial_${contest?.league}`);
    const today = new Date();

    if (lastShown) {
      const lastShownDate = new Date(lastShown);
      const diffTime = Math.abs(today.getTime() - lastShownDate.getTime());
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      if (diffDays >= 3) {
        setShowTutorial(true);
        localStorage.setItem(`sbc_tutorial_${contest?.league}`, today.toDateString());
      }
    } else {
      setShowTutorial(true);
    }
  }, []);

  useEffect(() => {
    if (isSpinOpen === null) {
      if (dailySpinStatus) {
        setIsSpinOpen(dailySpinStatus.canSpinToday);
      } else {
        checkSpin();
      }
    }
  }, [dailySpinStatus, isSpinOpen, setIsSpinOpen]);

  const handleTutorialToggle = () => {
    const today = new Date();
    setShowTutorial((prev) => !prev);
    localStorage.setItem(`sbc_tutorial_${contest?.league}`, today.toDateString());
  };

  const tutorialTitle = () => {
    if (contest?.league === 'nba' || contest?.league === 'wnba') {
      return 'Go Big on Points';
    } else if (contest?.league === 'mlb') {
      return 'Aim for max total bases & hits';
    } else if (contest?.league === 'nfl') {
      return 'Rack up the most yards today';
    }
  };

  const toturialContent = () => {
    if (contest?.league === 'nba' || contest?.league === 'wnba') {
      return (
        <Flex flexDirection="column" gap="32px">
          <Text variant="tutorialText">
            🃏 <b>Start 3 + 1 backup.</b> Tap cards for season stats
          </Text>
          <Text variant="tutorialText">
            🚀 <b>Score as they play:</b> 1 pt for every point scored in real game
          </Text>
          <Text variant="tutorialText">
            ⚡️ <b>Boost for extra points or re-roll picks before submit</b>
          </Text>
        </Flex>
      );
    } else if (contest?.league === 'mlb') {
      return (
        <Flex flexDirection="column" gap="32px">
          <Text variant="tutorialText">
            🃏 <b>Start 3 + 1 backup.</b> Tap cards for season stats
          </Text>
          <Text variant="tutorialText">
            🚀 <b>Score as they play:</b> 10 pts for every hit and total base
          </Text>
          <Text variant="tutorialText">
            ⚡️ <b>Boost for extra points or re-roll picks before submit</b>
          </Text>
        </Flex>
      );
    } else if (contest?.league === 'nfl') {
      return (
        <Flex flexDirection="column" gap="32px">
          <Text variant="tutorialText">
            🃏 <b>Start 3 + 1 backup.</b> from 7 players. Tap for season stats
          </Text>
          <Text variant="tutorialText">
            🚀 <b>Score as they play:</b> 1 pt per rush/rec yd, 0.3 per pass yd
          </Text>
          <Text variant="tutorialText">
            ⚡️ <b>Boost for extra points or re-roll picks before submit</b>
          </Text>
        </Flex>
      );
    }
  };

  if (participation === null || isSpinOpen === null || isFetchingParticipation) {
    return <Loader />;
  }

  return (
    <>
      {isSpinOpen !== null && (
        <>
          {isSpinOpen && <Spinner onClose={() => setIsSpinOpen(false)} dailySpinStatus={dailySpinStatus} />}
          {!isSpinOpen && (
            <Fade
              unmountOnExit
              in={showTutorial}
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                zIndex: 999,
              }}
            >
              <Tutorial
                onOk={handleTutorialToggle}
                title={
                  <Flex alignItems="center" justifyContent="center" flexDirection="column" gap="12px">
                    <Text variant="tutorialTitle">{tutorialTitle()}</Text>
                  </Flex>
                }
                content={toturialContent()}
              />
            </Fade>
          )}

          <Builder
            cardsWithStatus={cardsWithStatus}
            selectedCards={selectedCards}
            card={card}
            contest={contest}
            benchCard={benchCard}
            chooseCard={chooseCard}
            animation={animation}
            participation={participation}
            isAnimating={isAnimating}
            showTutorial={!showTutorial && !isSpinOpen && isSpinOpen !== null}
            showTutorialToggle={handleTutorialToggle}
          />
        </>
      )}
    </>
  );
};

export default BuilderPage;
